import { Fragment, useEffect, useMemo, useState } from "react";
import { useEditor } from "../state/editor";
import { calcularCotizacion } from "../domain/pricing";
import { moneda } from "../domain/model";
import type { Vendedor } from "../domain/types";
import { Modal, Porcentaje } from "./ui";
import { HojaEscalada } from "./HojaPdf";
import { VistaPreviaPdf } from "./Margen";
import Icono from "./Icono";

// Pestaña elegida (Resumen / Vista PDF): se recuerda en este equipo.
const CLAVE_VISTA = "cotizador_vista_resumen";
function vistaGuardada(): "resumen" | "pdf" {
  try {
    return localStorage.getItem(CLAVE_VISTA) === "pdf" ? "pdf" : "resumen";
  } catch {
    return "resumen";
  }
}
const pct = (f: number) =>
  `${(f * 100).toLocaleString("es-EC", { maximumFractionDigits: 2 })} %`;
export default function Resumen({ vendedor }: { vendedor: Vendedor | null }) {
  const [vista, setVista] = useState(vistaGuardada),
    [ampliada, setAmpliada] = useState(false),
    [pdfReal, setPdfReal] = useState(false),
    // Celular: el resumen es una hoja que se abre desde la barra del total.
    [hoja, setHoja] = useState(false);
  useEffect(() => {
    document.documentElement.toggleAttribute("data-hoja-abierta", hoja);
    return () => document.documentElement.removeAttribute("data-hoja-abierta");
  }, [hoja]);
  const elegirVista = (v: "resumen" | "pdf") => {
    setVista(v);
    try {
      localStorage.setItem(CLAVE_VISTA, v);
    } catch {
      // Sin almacenamiento: la pestaña vale solo para esta carga.
    }
  };
  const enfocarPieza = useEditor((s) => s.enfocarPieza),
    irAPieza = (id: string) => {
      setAmpliada(false);
      setHoja(false);
      enfocarPieza(id);
    };
  const c = useEditor((s) => s.cotizacion),
    patch = useEditor((s) => s.patch),
    cambios = useEditor((s) => s.preciosActualizados),
    r = useMemo(() => calcularCotizacion(c), [c]);
  const filas = [
    // Sin desglose de imprevistos ni mano de obra: el catálogo ya es precio
    // de venta y el vendedor no debe ver la fórmula interna.
    ["Productos", r.productos],
    ["Margen", r.margen],
    ["Add-ons y logística", r.logistica],
    ["Descuentos por pieza", -r.descuentosPieza],
    // Con el porcentaje y dónde se cambia: el descuento general se fija en
    // el paso 3 y antes aparecía aquí solo como monto, sin pista de origen.
    [
      `Descuento general (${pct(c.descuento)}, se cambia en el paso 3)`,
      -r.descuento,
    ],
    [`IVA (${c.ivaPct} %)`, r.iva],
  ] as const;
  // Monto de cada producto / detalle bajo «Productos», sin margen: las
  // líneas suman exactamente esa fila y el margen sigue en la suya.
  const porPieza = new Map(r.piezas.map((p) => [p.id, p])),
    variosSitios = c.sitios.length > 1,
    desglose = c.sitios
      .flatMap((s, i) =>
        s.productos.map((pr) => ({
          id: pr.id,
          nombre:
            (variosSitios ? `${s.nombre.trim() || `Sitio ${i + 1}`} · ` : "") +
            (pr.nombre.trim() || "Producto sin nombre"),
          monto: pr.piezas.reduce(
            (n, p) => n + (porPieza.get(p.id)?.costo ?? 0),
            0,
          ),
        })),
      )
      .filter((d) => d.monto !== 0);
  return (
    <>
      <button
        type="button"
        className="resumen-mini"
        aria-expanded={hoja}
        aria-controls="resumen-cotizacion"
        onClick={() => setHoja(true)}
      >
        <span className="resumen-mini-etiqueta">
          {c.ivaIncluido ? "Total con IVA" : "Precio de venta"}
        </span>
        <strong>{moneda(r.total)}</strong>
        <span className="resumen-mini-ver">
          Ver resumen <Icono nombre="bajar" />
        </span>
      </button>
      <aside
        className="card summary"
        id="resumen-cotizacion"
        data-vista={vista}
        data-hoja={hoja || undefined}
      >
        <button
          type="button"
          className="resumen-cerrar icon-button ghost"
          aria-label="Cerrar resumen"
          onClick={() => setHoja(false)}
        >
          <Icono nombre="cerrar" />
        </button>
        <p className="eyebrow">Tu propuesta</p>
        <div className="resumen-pestanas" role="tablist" aria-label="Vista">
          {(
            [
              ["resumen", "Resumen"],
              ["pdf", "Vista PDF"],
            ] as const
          ).map(([v, texto]) => (
            <button
              key={v}
              role="tab"
              aria-selected={vista === v}
              className={vista === v ? "activa" : undefined}
              onClick={() => elegirVista(v)}
            >
              {texto}
            </button>
          ))}
        </div>
        {vista === "pdf" ? (
          <div className="resumen-pdf">
            <HojaEscalada c={c} vendedor={vendedor} onIrAPieza={irAPieza} />
            <div className="row">
              <button onClick={() => setAmpliada(true)}>
                <Icono nombre="buscar" />
                Ampliar
              </button>
              {vendedor ? (
                <button onClick={() => setPdfReal(true)}>
                  <Icono nombre="archivo" />
                  Ver PDF real
                </button>
              ) : null}
            </div>
            <small>
              Vista aproximada que cambia mientras editas: haz clic en una pieza
              para ir a ella. El PDF que se entrega lo genera el sistema con los
              precios validados: «Ver PDF real».
            </small>
            {ampliada ? (
              <Modal
                title="Vista PDF"
                onClose={() => setAmpliada(false)}
                className="pdf-preview"
              >
                <div className="hoja-ampliada">
                  <HojaEscalada
                    c={c}
                    vendedor={vendedor}
                    onIrAPieza={irAPieza}
                  />
                </div>
              </Modal>
            ) : null}
            {pdfReal && vendedor ? (
              <VistaPreviaPdf
                vendedor={vendedor}
                onClose={() => setPdfReal(false)}
              />
            ) : null}
          </div>
        ) : (
          <>
            <h2>Resumen de la cotización</h2>
            <dl>
              {filas
                .filter(([, n]) => n !== 0)
                .map(([label, n]) => (
                  <Fragment key={label}>
                    <div>
                      <dt>{label}</dt>
                      <dd>{moneda(n)}</dd>
                    </div>
                    {label === "Productos"
                      ? desglose.map((d) => (
                          <div className="desglose" key={d.id}>
                            <dt>{d.nombre}</dt>
                            <dd>{moneda(d.monto)}</dd>
                          </div>
                        ))
                      : null}
                  </Fragment>
                ))}
            </dl>
            {cambios.length ? (
              <p className="notice" role="status">
                Precios actualizados al catálogo vigente:{" "}
                {cambios
                  .map(
                    (x) => `${x.pieza} ${moneda(x.antes)} → ${moneda(x.ahora)}`,
                  )
                  .join("; ")}
                . Es el precio con el que se valida y guarda.
              </p>
            ) : null}
            <div className="total">
              <span>
                {c.ivaIncluido ? "Total incluido IVA" : "Precio de venta"}
              </span>
              <strong key={r.total} data-testid="cotizacion-total">
                {moneda(r.total)}
              </strong>
            </div>
            {/* El mismo «Incluir IVA» del paso 3, a la vista desde cualquier
                paso: el equipo no encontraba cómo mostrar el valor con IVA. */}
            <label className="check">
              <input
                type="checkbox"
                checked={c.ivaIncluido}
                onChange={(e) => patch({ ivaIncluido: e.target.checked })}
              />
              Incluir IVA{c.ivaIncluido ? ` (${c.ivaPct} %)` : ""}
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={c.margenActivo}
                onChange={(e) => patch({ margenActivo: e.target.checked })}
              />
              Aplicar margen general
              {c.margenActivo
                ? ` (${pct(c.margen)} sobre el precio de venta)`
                : " (apagado)"}
            </label>
            {c.margenActivo ? (
              <Porcentaje
                label="Margen general (%)"
                value={c.margen}
                onChange={(n) => patch({ margen: n ?? 0 })}
              />
            ) : null}
            <small>
              Los márgenes por sitio o pieza tienen prioridad. Los precios de
              venta manuales y de contrato no reciben margen.
            </small>
            {r.sitios.length > 1 ? (
              <dl>
                {r.sitios.map((s, i) => (
                  <div key={s.id}>
                    <dt>{c.sitios[i].nombre || `Sitio ${i + 1}`}</dt>
                    <dd>{moneda(s.total)}</dd>
                  </div>
                ))}
              </dl>
            ) : null}
          </>
        )}
      </aside>
    </>
  );
}
